import { isAction, type Middleware } from '@reduxjs/toolkit';

import type { Epic, StateChangedBy } from 'Core/epics/common';
import type { ReduxStateType, Selector } from 'Core/store';
import { logEpicError } from 'Diagnostics/diagnostics';

export type { EpicArg, StateChangedBy } from 'Core/epics/common';

// Middleware's first generic is an "extra dispatch API". Toolkit's default extends
// Dispatch<UnknownAction>, so passing `Record<string, never>` would require the
// middleware to *narrow* dispatch to an empty object and breaks thunk-dispatch
// composition. `{}` here means "no extra dispatch API", which is the intended meaning.
export default (epics: Epic[]): Middleware<{}, ReduxStateType> =>
  (store) =>
  (next) =>
  (action) => {
    const { dispatch } = store;
    const prevState = store.getState();
    const result = next(action);

    if (!isAction(action)) {
      return result;
    }

    const state = store.getState();

    const stateChangedBy = createStateChangedBy(prevState, state);

    epics.forEach(async (epic) => {
      try {
        const nextAction = await epic({ state, dispatch, stateChangedBy, action });
        if (nextAction) {
          dispatch(nextAction);
        }
      } catch (err) {
        logEpicError({
          epicName: epic.name || 'anonymous',
          action: action.type,
          error: err,
        });
      }
    });

    return result;
  };

function createStateChangedBy(prevState: ReduxStateType, state: ReduxStateType): StateChangedBy {
  const cache = new Map<Selector<unknown>, { matched: boolean }>();
  return (selector: Selector<unknown>): boolean => {
    const cached = cache.get(selector);
    if (cached) {
      return cached.matched;
    }
    const matched = selector(prevState) !== selector(state);
    cache.set(selector, { matched });
    return matched;
  };
}
